import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { echartsContent } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import { reviewsFormat, scoreFormat } from '@/pages/opinion/utils/value';
import { getUtcMinusTime, momentTimeAAfterB } from '@op/utils/time';
import { dealwithTimeline } from '@op/pages/SteamPerformance/utils';
import { getChartPositionTop } from '../utils';

export default function GoogleTrendsChart({
  filter = {},
  newValue = [],
  oldValue = [],
  valueKey,
  list,
  leftTitle,
  formatValue = reviewsFormat,
  yAxisFormatValue = echartsContent,
  height,
}: any) {
  const { formatMessage } = useIntl();

  const getOptions = () => {
    const utcMinusTime = getUtcMinusTime(filter);
    const { oldYAxis, yAxis, xAxis, timeMapValue } = dealwithTimeline(
      newValue,
      oldValue,
      valueKey,
    );
    const series: {
      name: string;
      type: string;
      data: any[];
      itemStyle: any;
      lineStyle: any;
      endLabel: any;
    }[] = [];
    list.forEach(({ name, color }: any, index: number) => {
      const isCurrent = index === 0;
      const data = (isCurrent ? yAxis : oldYAxis) || [];
      const maxValue = Math.max(
        ...data.map((item) => (typeof item === 'number' ? item : 0)),
      );

      let item: any = {
        name: name,
        type: 'line',
        data: data.map((item) => {
          const isMax = item === maxValue && item !== 0;
          return {
            value: item,
            label: {
              show: isMax && index === 0,
              color: '#000',
              fontFamily: 'num-m',
              formatter: (params: any) => {
                const value = params.value;
                return scoreFormat(value, 0);
              },
            },
            itemStyle: {
              opacity: isMax ? 1 : 0,
            },
          };
        }),
        itemStyle: {
          color,
        },
        lineStyle: {
          type: isCurrent ? 'solid' : 'dashed',
        },
      };

      if (isCurrent) {
        item.data = item.data.map((item: number, index: number) => {
          const currentTime = xAxis[index];

          if (momentTimeAAfterB(utcMinusTime, currentTime)) {
            return item;
          }

          return null;
        });
      }

      series.push(item);
    });
    series.reverse();

    return {
      tooltip: {
        position: (pos, params, dom, rect, size) => getChartPositionTop(pos, size),
        confine: false,  
        formatter: (params: any) => {
          const time = params?.[0]?.axisValue;
          let title = listTimeTitleFunc(time, filter.date_type, true);
          const otherTitle = listTimeTitleFunc(
            timeMapValue?.[time],
            filter?.date_type,
            true,
          );

          return formatChartParamsDate({
            params,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return formatMessage({ id: name || NO_DATE.EMPTY });
            },
            valueFormat: (value) => {
              return scoreFormat(value, 0);
            },
            addExtraInfo: (element: any) => {
              return { name: element.seriesName };
            },
            tablesConfigFunc: (records: Array<any>) => {
              const periodElement = records.find(
                (item) => item.name === list[0].name,
              );

              const beforeElement = records.find(
                (item) => item.name === list[1].name,
              );

              const columns = [];

              if (periodElement) {
                columns.push({
                  showHeader: true,
                  records: [periodElement],
                  columns: [
                    {
                      key: 'text',
                      header: title,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: leftTitle }),
                    },
                  ],
                });
              }
              if (beforeElement) {
                columns.push({
                  showHeader: true,
                  records: [beforeElement],
                  columns: [
                    {
                      key: 'text',
                      header: otherTitle,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: ' ',
                    },
                  ],
                });
              }
              return columns;
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter?.date_type),
      ),
      grid: {
        top: 30,
        right: 15,
        bottom: 0,
        containLabel: true,
      },
      animation: false,
      legend: {
        show: false,
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          fontSize: 8,
          color: "rgba(38, 40, 50, 0.6)",
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          splitNumber: 5, 
          minInterval: 1,
          axisLabel: {
            fontSize: 8,
            color: 'rgba(0, 0, 0, 0.6)',
            formatter: (value: any) => yAxisFormatValue(value),
          },
        },
      ],
      series: series,
    };
  };

  const empty = useMemo(() => {
    const newEmpty = newValue.every((item: any) => item[valueKey] === 0);
    const oldEmpty = oldValue.every((item: any) => item[valueKey] === 0);
    return newEmpty && oldEmpty;
  }, [newValue, oldValue]);

  return (
    <EChartCommon
      options={getOptions()}
      height={height}
      empty={empty}
      hasEmptyDesc={false}
      optionsProps={{ type: 'line' , excludeOptions: ['axisPointer'] }}
    ></EChartCommon>
  );
}
